/**
 * Library settings — how packed tag values are split, and whether Nova watches
 * the library folders for changes.
 *
 * These are library-wide, not per-viewer: they change what the data *means*, so
 * two curators looking at the same library must see the same chips. They live in
 * the database alongside custom columns, unlike column widths which are a local
 * convenience.
 *
 * **The dialog is generated from the engine's own description of itself.** The
 * engine reports which separators it supports for each field, the literal token
 * each one stands for, and which are protected and why. Hardcoding that list here
 * would guarantee it drifts the first time the engine gains a separator.
 */

import { useEffect, useState, type ReactNode } from "react";
import type { SettingsPayload, WatchStatus } from "./api";

/** Human labels for the setting keys the engine exposes. */
const GROUP_LABELS: Record<string, { title: string; note: string }> = {
  "split.genre.separators": {
    title: "Genre separators",
    note: "Characters that split a packed genre tag into separate chips.",
  },
  "split.comment.separators": {
    title: "Comment separators",
    note: "Many libraries use the comment field as an ad-hoc list of moods or cues.",
  },
  "split.artist.separators": {
    title: "Artist separators",
    note: "Markers that indicate a featured or collaborating artist.",
  },
};

export function Settings({
  payload,
  watch,
  onSave,
  onSaveWatch,
  onSweep,
  onClose,
  embedded = false,
  separators = true,
  showWatch = true,
}: {
  payload: SettingsPayload | null;
  watch: WatchStatus | null;
  onSave: (next: Record<string, string[]>) => Promise<string | null>;
  onSaveWatch: (changes: {
    enabled?: boolean;
    sweepMinutes?: number;
  }) => Promise<string | null>;
  onSweep: () => Promise<string | null>;
  onClose: () => void;
  /** Render as a page inside the Settings window rather than as its own dialog. */
  embedded?: boolean;
  /** The artist/genre separators (POST /api/settings needs settings.manage). */
  separators?: boolean;
  /** Watching the folders (library.folders). */
  showWatch?: boolean;
}) {
  const [draft, setDraft] = useState<Record<string, string[]>>(
    () => payload?.settings ?? {}
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(payload?.settings ?? {});
    setSaved(false);
  }, [payload]);

  if (!payload && embedded) {
    return <p className="note">Library settings live in the library database, so the engine has to be running to read or change them.</p>;
  }

  if (!payload) {
    return (
      <>
        <div className="scrim" onClick={onClose} />
        <div className="popover settings-popover">
          <div className="popover-head">
            <span>Settings</span>
            <button className="icon-button" onClick={onClose} title="Close">×</button>
          </div>
          <div className="popover-body">
            <p className="note">
              Settings live in the library database, so the engine has to be
              running to read or change them.
            </p>
          </div>
        </div>
      </>
    );
  }

  const toggle = (key: string, name: string) =>
    setDraft((d) => {
      const enabled = d[key] ?? [];
      return {
        ...d,
        [key]: enabled.includes(name)
          ? enabled.filter((n) => n !== name)
          : [...enabled, name],
      };
    });

  const dirty = JSON.stringify(draft) !== JSON.stringify(payload.settings);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    const err = await onSave(draft);
    setError(err);
    setSaved(err === null);
    setSaving(false);
  };

  const Outer = embedded ? EmbeddedFrame : DialogFrame;
  return (
    <Outer onClose={onClose}>
        <div className="popover-body">
          {separators && Object.entries(payload.options).map(([key, spec]) => {
            const meta = GROUP_LABELS[key] ?? { title: key, note: "" };
            const enabled = draft[key] ?? [];
            const isDefault =
              JSON.stringify([...enabled].sort()) ===
              JSON.stringify([...spec.default].sort());

            return (
              <div className="settings-group" key={key}>
                <div className="popover-section" style={{ marginTop: 0 }}>
                  {meta.title}
                  {!isDefault && <em> · changed</em>}
                </div>
                {meta.note && (
                  <p className="note" style={{ marginTop: -3 }}>{meta.note}</p>
                )}
                <div className="sep-choices">
                  {spec.available.map((opt) => (
                    <label
                      key={opt.name}
                      className="sep-choice"
                      title={`${opt.name} — splits on ${JSON.stringify(opt.token)}`}
                    >
                      <input
                        type="checkbox"
                        checked={enabled.includes(opt.name)}
                        onChange={() => toggle(key, opt.name)}
                      />
                      <code>{opt.token.trim() || opt.name}</code>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {showWatch && <WatchSettings watch={watch} onSave={onSaveWatch} onSweep={onSweep} />}

          {separators && payload.protected.length > 0 && (
            <p className="note settings-guard">
              Always on, whatever you choose here:{" "}
              {payload.protected.map((p, i) => (
                <span key={p.name}>
                  {i > 0 && ", "}
                  <code title={p.reason}>{p.name}</code>
                </span>
              ))}
              . Separator choices also never override the guards that keep{" "}
              <code>AC/DC</code> and <code>Drum &amp; Bass</code> intact — those
              stop data being destroyed rather than expressing a preference.
            </p>
          )}
        </div>

        {separators && <div className="popover-foot settings-foot">
          {error && <div className="write-result bad">{error}</div>}
          {saved && !dirty && (
            <div className="write-result good">
              Saved. Chips re-split on the next refresh.
            </div>
          )}
          <div className="inspector-actions">
            {!embedded && <button className="ghost" onClick={onClose}>Close</button>}
            <button className="primary" onClick={save} disabled={saving || !dirty}>
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>}
    </Outer>
  );
}

function DialogFrame({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="popover settings-popover" role="dialog" aria-label="Settings">
        <div className="popover-head">
          <span>Settings</span>
          <button className="icon-button" onClick={onClose} title="Close">×</button>
        </div>
        {children}
      </div>
    </>
  );
}

function EmbeddedFrame({ children }: { onClose: () => void; children: ReactNode }) {
  return <div className="settings-embedded">{children}</div>;
}


/**
 * Whether Nova notices files appearing and vanishing on its own.
 *
 * The status line below reports the watcher and the sweep separately rather
 * than as one "on". They fail independently and mean different things: the
 * watcher is what makes a dropped file appear in seconds, the sweep is what
 * makes the grid *correct* — filesystem notifications get dropped under load,
 * silently, so the sweep is the half that catches what was never reported. A
 * curator whose watcher has quietly failed on an unplugged drive should be able
 * to see that here rather than infer it from a library that stopped updating.
 */
function WatchSettings({
  watch,
  onSave,
  onSweep,
}: {
  watch: WatchStatus | null;
  onSave: (changes: { enabled?: boolean; sweepMinutes?: number }) => Promise<string | null>;
  onSweep: () => Promise<string | null>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [swept, setSwept] = useState(false);

  if (!watch) return null;

  const apply = async (changes: { enabled?: boolean; sweepMinutes?: number }) => {
    setBusy(true);
    setError(await onSave(changes));
    setBusy(false);
  };

  const sweep = async () => {
    setBusy(true);
    setSwept(false);
    const err = await onSweep();
    setError(err);
    setSwept(err === null);
    setBusy(false);
  };

  return (
    <div className="settings-group">
      <div className="popover-section">Watch folders for changes</div>
      <p className="note" style={{ marginTop: -3 }}>
        Index files as they are added, changed or removed, without waiting for a
        scan.
      </p>

      <label className="sep-choice" style={{ marginBottom: 6 }}>
        <input
          type="checkbox"
          checked={watch.enabled}
          disabled={busy || !watch.available}
          onChange={(e) => apply({ enabled: e.target.checked })}
        />
        <span>Watch the library</span>
      </label>

      <label className="watch-interval">
        <span>Full check every</span>
        <select
          value={watch.sweepMinutes}
          disabled={busy}
          onChange={(e) => apply({ sweepMinutes: Number(e.target.value) })}
        >
          {[5, 15, 30, 60, 240].map((minutes) => (
            <option key={minutes} value={minutes}>
              {minutes < 60
                ? `${minutes} minutes`
                : `${minutes / 60} hour${minutes === 60 ? "" : "s"}`}
            </option>
          ))}
        </select>
      </label>

      <p className="note">{describe(watch)}</p>
      {watch.stats.lastSweepSummary && (
        <p className="note watch-summary" title={watch.stats.lastSweepSummary}>
          Last check — {watch.stats.lastSweepSummary}
        </p>
      )}
      {watch.error && <div className="write-result bad">{watch.error}</div>}
      {error && <div className="write-result bad">{error}</div>}
      {swept && !error && (
        <div className="write-result good">Check queued.</div>
      )}

      <button className="ghost" onClick={sweep} disabled={busy}>
        Check now
      </button>
    </div>
  );
}

/** One sentence for the state the watch is actually in. */
function describe(watch: WatchStatus): string {
  if (!watch.available) {
    return `No file watcher available on this machine${
      watch.unavailableReason ? ` (${watch.unavailableReason})` : ""
    }, so changes are picked up by the periodic check alone.`;
  }
  if (!watch.enabled) {
    return "Not watching. Changes are found by the periodic check, and by scanning.";
  }
  if (!watch.watching) {
    return "Enabled, but not watching any folder yet — add a library folder, or reconnect a drive that has gone away.";
  }
  const folders = watch.roots.length === 1 ? "1 folder" : `${watch.roots.length} folders`;
  const pending = watch.pending > 0 ? `; ${watch.pending} settling` : "";
  return `Watching ${folders}${pending}. The periodic check catches anything the system does not report.`;
}
